import React, { createRef, /* useEffect, */ useState } from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  useTheme,
  Box,
  Popover,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
  Switch,
  ListItemSecondaryAction,
  ListItem,
  List,
  Chip,
  Skeleton,
  Paper
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  usePopupState,
  bindTrigger,
  bindMenu
} from 'material-ui-popup-state/hooks';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Settings as CogIcon } from 'react-feather';
import SunIcon from '@mui/icons-material/Brightness7';
import MoonIcon from '@mui/icons-material/Brightness4';
import useSettingsContext from 'src/hooks/useSettingsContext';
import useSessionContext from 'src/hooks/useSessionContext';
import { Squash as Hamburger } from 'hamburger-react';
import { Steps } from 'intro.js-react';
import { THEMES /* , STORAGE_KEYS */ } from 'src/constants';
// import { useLocalStorage } from 'src/hooks/useLocalStorage';
import SettingsOverscanIcon from '@mui/icons-material/SettingsOverscan';
import AnimalAvatar from './avatars/AnimalAvatar';
import NavBarTooltip from './tooltip/NavBarTooltip';

// const { THEME_SWITCHER } = STORAGE_KEYS;
const useStyles = makeStyles((theme) => ({
  avatar: {
    cursor: 'pointer',
    width: theme.spacing(5.5),
    height: theme.spacing(5.5)
    // color: theme.palette.custom.navbarMenuItemActive
  },
  email: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%'
  },
  userName: {
    textOverflow: 'ellipsis',
    maxWidth: '128px',
    overflow: 'hidden'
  },
  chip: {
    height: '20px',
    fontSize: '12px',
    fontWeight: '400',
    marginLeft: '8px'
  }
}));

const UserProfile = ({ drawerMode }) => {
  const { session } = useSessionContext();
  const user = session?.user;
  const { settings, saveSettings } = useSettingsContext();
  const classes = useStyles();
  const theme = useTheme();
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'userProfileMenu'
  });
  const [showIntro, setShowIntro] = useState(false);
  const buttonRef = createRef();
  const navigate = useNavigate();
  // const { value: isViewed, setValue } = useLocalStorage(THEME_SWITCHER, false);
  const bgColor =
    theme.palette.mode === 'light'
      ? theme.palette.background.menuItemActive
      : theme.palette.background.paper;

  const introDone = (e) => {
    setShowIntro(false);
    // setValue(true);

    setTimeout(() => {
      popupState.close(e);
    }, 100);
  };

  const userProfile = () => (
    <Paper
      {...bindTrigger(popupState)}
      ref={buttonRef}
      sx={{
        m: 2,
        p: 1.5,
        backgroundColor: bgColor,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        '&:hover': {
          cursor: 'pointer'
        },
        justifyContent: drawerMode === 'full' ? 'unset' : 'center'
      }}
    >
      <AnimalAvatar seed={user.email} className={classes.avatar} />

      {drawerMode === 'full' && (
        <Box
          ml={1}
          display="flex"
          justifyContent="space-between"
          flexGrow={1}
          alignItems="center"
        >
          <Typography variant="h5" className={classes.userName}>
            {user.given_name}
          </Typography>

          <Hamburger toggled={popupState.isOpen} size={20} />
        </Box>
      )}
    </Paper>
  );

  // useEffect(() => {
  //   if (buttonRef.current && !isViewed) {
  //     popupState.open(buttonRef.current);
  //   }
  // }, [isViewed, buttonRef]);

  if (!user) {
    return (
      <>
        <Skeleton variant="circular" width={64} height={64} animation="wave" />
        <Skeleton animation="wave" width="80%" />
        <Skeleton animation="wave" width="90%" />
      </>
    );
  }

  return (
    <>
      {drawerMode === 'full' ? (
        userProfile(popupState)
      ) : (
        <NavBarTooltip title="User Menu" placement="right">
          {userProfile(popupState)}
        </NavBarTooltip>
      )}

      <Popover
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom'
        }}
        {...bindMenu(popupState)}
        PaperProps={{
          style: { width: '330px' }
        }}
        // TransitionProps={{
        //   onEntered: () => {
        //     if (!isViewed) {
        //       setShowIntro(true);
        //     }
        //   }
        // }}
      >
        <Box p={2}>
          <Typography color="textPrimary" variant="subtitle2">
            {user.email}
          </Typography>
        </Box>
        <Divider />
        <Box m={2}>
          <List>
            <ListItem button component={RouterLink} to="/app/manage/profile">
              <ListItemIcon>
                <CogIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Profile" />
            </ListItem>

            <ListItem id="theme-switcher-item">
              <ListItemIcon>
                {settings.theme === THEMES.LIGHT ? <SunIcon /> : <MoonIcon />}
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    Dark Mode
                    <Chip
                      className={classes.chip}
                      color="primary"
                      size="small"
                      label="Beta"
                    />
                  </>
                }
              />
              <ListItemSecondaryAction>
                <Switch
                  color="secondary"
                  edge="end"
                  onChange={(e, checked) => {
                    saveSettings({
                      ...settings,
                      theme: checked ? THEMES.DARK : THEMES.LIGHT
                    });
                  }}
                  checked={settings.theme === THEMES.DARK}
                />
              </ListItemSecondaryAction>
            </ListItem>

            <ListItem id="layout-switcher">
              <ListItemIcon>
                <SettingsOverscanIcon />
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    Layout Stretch
                    <Chip
                      className={classes.chip}
                      color="primary"
                      size="small"
                      label="Beta"
                    />
                  </>
                }
              />
              <ListItemSecondaryAction>
                <Switch
                  color="secondary"
                  edge="end"
                  onChange={(e, checked) => {
                    saveSettings({
                      ...settings,
                      compact: !checked
                    });
                  }}
                  checked={!settings.compact}
                />
              </ListItemSecondaryAction>
            </ListItem>
          </List>
        </Box>
        <Box p={2}>
          <Button
            color="inherit"
            fullWidth
            variant="outlined"
            onClick={() => navigate('/logout')}
          >
            Logout
          </Button>
        </Box>
      </Popover>

      {showIntro && (
        <Steps
          enabled={showIntro}
          steps={[
            {
              element: '#theme-switcher-item',
              intro: 'Dark mode is now available in Bootstrapper.'
            }
          ]}
          initialStep={0}
          onExit={introDone}
          options={{
            showBullets: false
          }}
        />
      )}
    </>
  );
};

UserProfile.propTypes = {
  drawerMode: PropTypes.string
};

export default UserProfile;
